/**
 * The Phase 5C operations: workflow automation — the catalogue and templates,
 * workflows and their versions, validation, activation, dry runs, executions
 * and approvals.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type AutomationWorkflow = Schemas['AutomationWorkflow'];
export type AutomationWorkflowState = Schemas['AutomationWorkflowState'];
export type AutomationVersion = Schemas['AutomationVersion'];
export type AutomationDefinition = Schemas['AutomationDefinition'];
export type AutomationCondition = Schemas['AutomationCondition'];
export type AutomationAction = Schemas['AutomationAction'];
export type AutomationValidation = Schemas['AutomationValidation'];
export type AutomationProblem = Schemas['AutomationProblem'];
export type AutomationExecution = Schemas['AutomationExecution'];
export type AutomationExecutionState = Schemas['AutomationExecutionState'];
export type AutomationActionExecution = Schemas['AutomationActionExecution'];
export type AutomationApproval = Schemas['AutomationApproval'];
export type AutomationCatalog = Schemas['AutomationCatalog'];
export type AutomationTriggerSpec = Schemas['AutomationTriggerSpec'];
export type AutomationActionSpec = Schemas['AutomationActionSpec'];
export type AutomationField = Schemas['AutomationField'];
export type AutomationTemplate = Schemas['AutomationTemplate'];

export interface AutomationPage<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): AutomationPage<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const org = (orgId: string) => ({ org_id: orgId });

export function getAutomationCatalog(orgId: string): Promise<AutomationCatalog> {
  return expect(
    http.GET('/organizations/{org_id}/automation/catalog', { params: { path: org(orgId) } }),
  );
}

export async function listAutomationTemplates(orgId: string): Promise<AutomationTemplate[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/automation/templates', { params: { path: org(orgId) } }),
  );
  return paged<AutomationTemplate>(value).data;
}

export async function listAutomationWorkflows(
  orgId: string,
  query: { state?: AutomationWorkflowState; repository_id?: string; cursor?: string } = {},
): Promise<AutomationPage<AutomationWorkflow>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/automation/workflows', {
      params: { path: org(orgId), query: { limit: 50, ...query } },
    }),
  );
  return paged<AutomationWorkflow>(value);
}

export function createAutomationWorkflow(
  orgId: string,
  body: {
    name: string;
    description?: string;
    repository_id?: string;
    definition: AutomationDefinition;
  },
): Promise<AutomationWorkflow> {
  return expect(
    http.POST('/organizations/{org_id}/automation/workflows', {
      params: { path: org(orgId) },
      body,
    }),
  );
}

export function getAutomationWorkflow(orgId: string, id: string): Promise<AutomationWorkflow> {
  return expect(
    http.GET('/organizations/{org_id}/automation/workflows/{automation_id}', {
      params: { path: { ...org(orgId), automation_id: id } },
    }),
  );
}

export function updateAutomationWorkflow(
  orgId: string,
  id: string,
  body: { name?: string; description?: string; definition?: AutomationDefinition },
): Promise<AutomationWorkflow> {
  return expect(
    http.PATCH('/organizations/{org_id}/automation/workflows/{automation_id}', {
      params: { path: { ...org(orgId), automation_id: id } },
      body,
    }),
  );
}

export function archiveAutomationWorkflow(orgId: string, id: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/automation/workflows/{automation_id}', {
      params: { path: { ...org(orgId), automation_id: id } },
    }),
  );
}

export async function listAutomationWorkflowVersions(
  orgId: string,
  id: string,
): Promise<AutomationVersion[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/automation/workflows/{automation_id}/versions', {
      params: { path: { ...org(orgId), automation_id: id }, query: { limit: 50 } },
    }),
  );
  return paged<AutomationVersion>(value).data;
}

export function validateAutomationWorkflow(orgId: string, id: string): Promise<AutomationVersion> {
  return expect(
    http.POST('/organizations/{org_id}/automation/workflows/{automation_id}/validate', {
      params: { path: { ...org(orgId), automation_id: id } },
    }),
  );
}

export function activateAutomationWorkflow(
  orgId: string,
  id: string,
  version = 0,
): Promise<AutomationWorkflow> {
  return expect(
    http.POST('/organizations/{org_id}/automation/workflows/{automation_id}/activate', {
      params: { path: { ...org(orgId), automation_id: id } },
      body: { version },
    }),
  );
}

export function disableAutomationWorkflow(orgId: string, id: string): Promise<AutomationWorkflow> {
  return expect(
    http.POST('/organizations/{org_id}/automation/workflows/{automation_id}/disable', {
      params: { path: { ...org(orgId), automation_id: id } },
    }),
  );
}

export function dryRunAutomationWorkflow(
  orgId: string,
  id: string,
  body: { version?: number; repository_id?: string; data: Record<string, unknown> },
): Promise<AutomationExecution> {
  return expect(
    http.POST('/organizations/{org_id}/automation/workflows/{automation_id}/dry-run', {
      params: { path: { ...org(orgId), automation_id: id } },
      body,
    }),
  );
}

export async function listAutomationExecutions(
  orgId: string,
  query: { workflow_id?: string; state?: AutomationExecutionState; cursor?: string } = {},
): Promise<AutomationPage<AutomationExecution>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/automation/executions', {
      params: { path: org(orgId), query: { limit: 25, ...query } },
    }),
  );
  return paged<AutomationExecution>(value);
}

export function getAutomationExecution(orgId: string, id: string): Promise<AutomationExecution> {
  return expect(
    http.GET('/organizations/{org_id}/automation/executions/{execution_id}', {
      params: { path: { ...org(orgId), execution_id: id } },
    }),
  );
}

export function cancelAutomationExecution(
  orgId: string,
  id: string,
  reason = '',
): Promise<AutomationExecution> {
  return expect(
    http.POST('/organizations/{org_id}/automation/executions/{execution_id}/cancel', {
      params: { path: { ...org(orgId), execution_id: id } },
      body: { reason },
    }),
  );
}

export function approveAutomationExecution(
  orgId: string,
  id: string,
  reason = '',
): Promise<AutomationExecution> {
  return expect(
    http.POST('/organizations/{org_id}/automation/executions/{execution_id}/approve', {
      params: { path: { ...org(orgId), execution_id: id } },
      body: { reason },
    }),
  );
}

export function rejectAutomationExecution(
  orgId: string,
  id: string,
  reason = '',
): Promise<AutomationExecution> {
  return expect(
    http.POST('/organizations/{org_id}/automation/executions/{execution_id}/reject', {
      params: { path: { ...org(orgId), execution_id: id } },
      body: { reason },
    }),
  );
}
